import { NgClass, NgTemplateOutlet } from "@angular/common";
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  Input,
  OnInit,
  signal,
  WritableSignal,
} from "@angular/core";
import { Router, RouterLink, RouterModule } from "@angular/router";
import { TranslateModule } from "@ngx-translate/core";
import { Permission } from "../../infrastructure/guards/permissions.enum";

import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { AuthorizationService } from "../../infrastructure/authorization.service";
import { DataTransferBadgeService } from "../../infrastructure/data-transfer-badge.service";
import { MenuItem } from "../../infrastructure/models/menu-item.model";
import { NewsStateService } from "../../infrastructure/news-state.service";
import { UserDataService } from "../../infrastructure/userData.service";
import { AuthenticationService } from "../../infrastructure/authentication.service";
import { BadgeComponent } from "../badge/badge.component";
import { LanguagePickerComponent } from "../language-picker/language-picker.component";
import { UserDropdownComponent } from "../user-dropdown/user-dropdown.component";

@Component({
  selector: "app-sidebar",
  templateUrl: "./sidebar.component.html",
  styleUrl: "./sidebar.component.scss",
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    TranslateModule,
    RouterLink,
    RouterModule,
    NgClass,
    NgTemplateOutlet,
    BadgeComponent,
    UserDropdownComponent,
    LanguagePickerComponent,
  ],
})
export class SidebarComponent implements OnInit {
  private readonly authService = inject(AuthenticationService);
  private readonly authorizationService = inject(AuthorizationService);
  private readonly userDataService = inject(UserDataService);
  public readonly newsStateService = inject(NewsStateService);
  public readonly dataTransferBadgeService = inject(DataTransferBadgeService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly router = inject(Router);

  @Input() isMobile = false;

  isCollapsed = signal<boolean>(false);
  expandedMenuIds = signal<string[]>([]);
  canActivateUserManagement = signal<boolean>(false);
  canActivateAdministration = signal<boolean>(false);
  canActivateAdminArea = signal<boolean>(false);
  canActivateProductGroups = signal<boolean>(false);
  canActivateRegistrationRequestManagement = signal<boolean>(false);
  canActivatePermissionPatternManagement = signal<boolean>(false);
  canSeeSupport = signal<boolean>(false);
  canSeeHelpAndContact = signal<boolean>(false);
  canSeeSalesAndConsulting = signal<boolean>(false);
  canSeeGeneralRequests = signal<boolean>(false);
  canSeeTechnicalSupport = signal<boolean>(false);
  canSeeTickets = signal<boolean>(false);
  canSeeDataTransfer = signal<boolean>(false);
  canSeeLicences = signal<boolean>(false);
  canSeeTq = signal<boolean>(false);
  canSeeReturnShipments = signal<boolean>(false);
  canSeeDocumentation = signal<boolean>(false);
  canSeeSoftware = signal<boolean>(false);
  canSeeFaq = signal<boolean>(false);

  canActivateUserAdministration = computed(
    () =>
      this.canActivateUserManagement() ||
      this.canActivateRegistrationRequestManagement() ||
      this.canActivatePermissionPatternManagement()
  );

  // Central place to maintain: add a new signal above and its Permission here, nowhere else.
  private readonly permissionSignals: readonly (readonly [
    WritableSignal<boolean>,
    Permission,
  ])[] = [
    [this.canActivateUserManagement, Permission.ViewUsers],
    [this.canActivateAdministration, Permission.ViewAdministration],
    [this.canActivateAdminArea, Permission.ViewAdminArea],
    [
      this.canActivateRegistrationRequestManagement,
      Permission.ViewRegistrationRequests,
    ],
    [
      this.canActivatePermissionPatternManagement,
      Permission.ViewPermissionPattern,
    ],
    [this.canActivateProductGroups, Permission.ViewProductGroups],
    [this.canSeeTickets, Permission.ViewServiceTickets],
    [this.canSeeSupport, Permission.ViewSupport],
    [this.canSeeHelpAndContact, Permission.ViewHelpAndContact],
    [this.canSeeSalesAndConsulting, Permission.ViewSalesAndConsulting],
    [this.canSeeGeneralRequests, Permission.ViewGeneralRequests],
    [this.canSeeTechnicalSupport, Permission.ViewTechnicalSupport],
    [this.canSeeDataTransfer, Permission.ViewDataTransfer],
    [this.canSeeSoftware, Permission.ViewSoftware],
    [this.canSeeLicences, Permission.ViewLicenses],
    [this.canSeeReturnShipments, Permission.ViewReturnShipments],
    [this.canSeeTq, Permission.ViewTrainingAndQualification],
    [this.canSeeDocumentation, Permission.ViewDocumentation],
    [this.canSeeFaq, Permission.ViewFaq],
  ];

  // Simple nav items with identical markup in mobile/desktop, rendered via navItemTpl.
  simpleNavItems: MenuItem[] = [
    {
      label: "ReturnShipments",
      icon: "fa-wrench",
      route: "/portal/return-shipments",
      isVisible: () => this.canSeeReturnShipments(),
    },
    {
      label: "Documentation",
      icon: "fa-file",
      route: "/portal/documentation",
      isVisible: () => this.canSeeDocumentation(),
    },
    {
      label: "Software",
      icon: "fa-download",
      route: "/portal/software",
      isVisible: () => this.canSeeSoftware(),
    },
    {
      label: "FAQ",
      icon: "fa-circle-question",
      route: "/portal/faq",
      isVisible: () => this.canSeeFaq(),
    },
  ];

  administrationMenu: MenuItem = {
    id: "admin",
    label: "Administration",
    icon: "fa-user-tie",
    isVisible: () => this.canActivateAdministration(),
    // Desktop collapsed dropdown historically used the broader permission check below.
    isVisibleCollapsed: () => this.canActivateUserAdministration(),
    children: [
      {
        label: "UserOverview",
        route: "/portal/administration/users",
        isVisible: () => this.canActivateUserManagement(),
      },
      {
        label: "RegistrationRequests",
        route: "/portal/administration/registrations",
        isVisible: () => this.canActivateRegistrationRequestManagement(),
      },
      {
        label: "PermissionPattern",
        route: "/portal/administration/permission-patterns",
        isVisible: () => this.canActivatePermissionPatternManagement(),
      },
      {
        label: "ProductGroups",
        route: "/portal/administration/productGroup",
        isVisible: () => this.canActivateProductGroups(),
        spanClass: "d-none d-sm-inline",
      },
      {
        label: "AdminArea",
        route: "/portal/administration/adminArea",
        isVisible: () => this.canActivateAdminArea(),
        spanClass: "d-none d-sm-inline",
      },
    ],
  };

  supportMenu: MenuItem = {
    id: "support",
    label: "Support",
    icon: "fa-headset",
    isVisible: () =>
      this.canSeeSupport() ||
      this.canSeeTechnicalSupport() ||
      this.canSeeSalesAndConsulting() ||
      this.canSeeGeneralRequests(),
    children: [
      {
        label: "TechnicalSupport",
        route: "/portal/technical-support",
        isVisible: () => this.canSeeTechnicalSupport(),
      },
      {
        label: "SalesAndConsulting",
        route: "/portal/sales-and-consulting",
        isVisible: () => this.canSeeSalesAndConsulting(),
      },
      {
        label: "GeneralRequests",
        route: "/portal/general-requests",
        isVisible: () => this.canSeeGeneralRequests(),
      },
    ],
  };

  serviceMenu: MenuItem = {
    id: "service",
    label: "Service",
    icon: "fa-gear",
    isVisible: () =>
      this.canSeeLicences() || this.canSeeTq() || this.canSeeDataTransfer(),
    hasBadge: () => this.dataTransferBadgeService.hasNewFiles(),
    children: [
      {
        label: "LicenseManagement",
        route: "/portal/licenses",
        isVisible: () => this.canSeeLicences(),
      },
      {
        label: "DataTransfer",
        route: "/portal/data-transfer",
        isVisible: () => this.canSeeDataTransfer(),
        hasBadge: () => this.dataTransferBadgeService.hasNewFiles(),
        linkClass: "nav-link px-0 d-flex flex-row align-items-center gap-2",
      },
      {
        label: "TrainingAndQualification",
        route: "/portal/training",
        isVisible: () => this.canSeeTq(),
      },
    ],
  };

  // Resolves effective visibility of a submenu for the given rendering mode.
  submenuVisible(
    menu: MenuItem,
    mode: "mobile" | "expanded" | "collapsed"
  ): boolean {
    if (mode === "collapsed" && menu.isVisibleCollapsed) {
      return menu.isVisibleCollapsed();
    }
    return menu.isVisible();
  }

  ngOnInit(): void {
    this.expandMenusForCurrentRoute();

    this.authService.currentUser
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.updatePermissions();
      });

    this.userDataService.userUpdate$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.updatePermissions();
      });
  }

  private updatePermissions(): void {
    for (const [permissionSignal, permission] of this.permissionSignals) {
      permissionSignal.set(this.authorizationService.hasPermission(permission));
    }
  }

  private expandMenusForCurrentRoute(): void {
    const url = this.router.url;
    const expandedIds: string[] = [];

    if (url.startsWith("/portal/administration")) {
      expandedIds.push("admin");
    } else if (
      url.startsWith("/portal/technical-support") ||
      url.startsWith("/portal/sales-and-consulting") ||
      url.startsWith("/portal/general-requests")
    ) {
      expandedIds.push("support");
    }

    if (
      url.startsWith("/portal/licenses") ||
      url.startsWith("/portal/data-transfer") ||
      url.startsWith("/portal/training")
    ) {
      expandedIds.push("service");
    }

    this.expandedMenuIds.set(expandedIds);
  }

  widthCssClass = computed(() => {
    return {
      "width-collapsed": this.isCollapsed(),
      "width-expanded": !this.isCollapsed(),
    };
  });

  containerCenterCssClass = computed(() => {
    return {
      "container-alignment-collapsed": this.isCollapsed(),
      "container-alignment-expanded": !this.isCollapsed(),
    };
  });

  iconCenterClass = computed(() => {
    return {
      "icon-alignment-collapsed": this.isCollapsed(),
      "icon-alignment-expanded": !this.isCollapsed(),
    };
  });

  currentYear = new Date().getFullYear();

  logout(event: Event) {
    event.preventDefault();
    this.authService.logout();
  }
}
